import * as React from 'react';
import {useSelector} from 'react-redux';

import {Assignables} from 'nutshell-core/assignables';
import {
    safelyGetFilterByKey,
    safelyGetRelationshipFilterIdsFromFilterValue,
} from 'nutshell-core/filter';
import {Routing} from 'nutshell-core/routing';
import type {FilterObject} from 'nutshell-core/types';

import {convertToModels} from 'shells/filter-capsule/convert-to-models';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';

import type {ThreadType} from 'nutshell-graphql-types';
import type {
    GetSessionUser_loggedInUser as User,
    GetInboxSidebarCounts_inboxThreadCounts_chat_userInboxCounts as UserInboxCounts,
} from 'nutshell-graphql-types';

import {appHistory} from '../../../history';
import type {NutshellState} from '../../../store';

import {useAddUserInbox, useRemoveUserInbox} from '../../inbox/graphql/hooks';

import {AddButton} from '.';

export const SCHEMA_FIELD = {
    name: 'owner',
    type: 'relationship',
    title: 'Assignee',
    description: 'Assignee of the thread',
    relationships: ['users'],
    isFilterable: true,
    isDisplayable: true,
    isSortable: true,
    isAndable: false,
    size: 'md',
    isSingleSelect: true,
} as const;

type Props = {
    filters: string[];
    user: User;
    onParamChange: (params: any, replace: boolean) => void;
    onRemoveAllFilters: () => void;
    activeFilters: FilterObject[];
    userInboxCounts: UserInboxCounts[];
    threadType: ThreadType | null | undefined;
    currentTab: string | null | undefined;
};

export const AddInboxGroup = (props: Props) => {
    const {addUserInbox} = useAddUserInbox();
    const {removeUserInbox} = useRemoveUserInbox();

    const assignableUsers = useSelector((state: NutshellState) =>
        Assignables.getEnabledUsersByPermission(state, 'canUseInbox')
    );
    const filteredAssignableUsers = assignableUsers.filter(
        (assignableUser) =>
            !props.filters.includes(assignableUser.id) && assignableUser.id !== props.user.id
    );

    const assigneeFilter = safelyGetFilterByKey(props.activeFilters, 'owner');
    const currentFilter = safelyGetRelationshipFilterIdsFromFilterValue(assigneeFilter || {})[0];

    return (
        <StackLegacy spacing={8}>
            {props.filters.map((filterId: string) => {
                const assignableUser = assignableUsers.find((u) => u.id === filterId);
                const userInboxCount =
                    props.userInboxCounts &&
                    props.userInboxCounts.find(
                        (user) =>
                            user.user &&
                            user.user.id === (assignableUser ? assignableUser.id : null)
                    );

                const isActive =
                    filterId === currentFilter &&
                    props.currentTab ===
                        (props.threadType ? props.threadType.toLowerCase() : 'all');

                return (
                    <AddButton
                        key={filterId}
                        isActive={isActive}
                        addButtonText='Add a user'
                        user={assignableUser}
                        userInboxCount={(userInboxCount && userInboxCount.count) || 0}
                        collection={convertToModels(filteredAssignableUsers) || []}
                        schemaField={SCHEMA_FIELD}
                        onSubmit={(newFilter?: FilterObject | null) => {
                            if (newFilter) {
                                props.onParamChange({filter: newFilter}, false);
                            } else {
                                removeUserInbox({
                                    threadType: props.threadType,
                                    userId: filterId,
                                });
                                props.onRemoveAllFilters();
                            }
                        }}
                        onClick={() => {
                            appHistory.push({
                                pathname: `/inbox/${
                                    props.threadType ? props.threadType.toLowerCase() : 'all'
                                }/open`,
                                search: Routing.param({
                                    filter: {
                                        owner: {
                                            anyAll: 'any',
                                            data: [{data: filterId}],
                                        },
                                    },
                                }),
                            });
                        }}
                    />
                );
            })}
            {filteredAssignableUsers.length > 0 && (
                <AddButton
                    isActive={false}
                    user={null}
                    addButtonText='Add a user'
                    collection={convertToModels(filteredAssignableUsers) || []}
                    schemaField={SCHEMA_FIELD}
                    onSubmit={(newFilter?: FilterObject | null) => {
                        if (newFilter) {
                            const newFilterId = safelyGetRelationshipFilterIdsFromFilterValue(
                                newFilter.owner
                            )[0];
                            addUserInbox({
                                threadType: props.threadType,
                                // @ts-expect-error
                                userId: newFilterId,
                            });
                            appHistory.push({
                                pathname: `/inbox/${
                                    props.threadType ? props.threadType.toLowerCase() : 'all'
                                }/open`,
                                search: Routing.param({
                                    filter: {
                                        owner: {
                                            anyAll: 'any',
                                            data: [{data: newFilterId}],
                                        },
                                    },
                                }),
                            });
                        }
                    }}
                />
            )}
        </StackLegacy>
    );
};
